import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Progress,
  Row,
  Select,
  Spin,
  Upload,
  message,
  notification,
} from "antd";
import { useEffect, useState } from "react";
import axios from "../../../config/axiosInstance";
import { API } from "../../../config/API";
import {
  uploadImageWithProgress,
  uploadVideoWithProgress,
} from "../../../config/s3config/s3fileUploadWithProgress";
import TextArea from "antd/es/input/TextArea";
import {
  InboxOutlined,
  DeleteOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import PageHeader from "../../../component/pageHeaders";
import { POST } from "../../../utils/apirequest";
import { useSelector } from "react-redux";
import { LoadTags } from "../../../utils/commonapis";

const { Dragger } = Upload;

function NewForm(props: any) {
  const User = useSelector((state: any) => state.User.user);
  const [form] = Form.useForm();

  const [submitLoading, setSubmitLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [imageUploadPercent, setImageUploadPercent] = useState(0);
  const [videoUploadPercent, setVideoUploadPercent] = useState(0);
  const [tagsLoading, setTagsLoading] = useState(true);

  const [description, setDescription] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [imageurl, setImageurl] = useState("");
  const [reelsId, setReelsId] = useState<any>(null);
  const [tags, setTags] = useState<any>([]);
  const [tagFilter, setTagFilters] = useState("");

  useEffect(() => {
    loadTags(tagFilter);
  }, [tagFilter]);

  const loadTags = async (searchTerm: any) => {
    setTagsLoading(true);
    try {
      const res: any = await LoadTags(searchTerm);
      setTags(res);
    } catch (error) {
      console.error("Error loading tags:", error);
    } finally {
      setTagsLoading(false);
    }
  };

  function debounce(func: any, delay: any) {
    let timer: any;
    return (...args: any) => {
      clearTimeout(timer);
      timer = setTimeout(() => func(...args), delay);
    };
  }

  const OnTagSearch = debounce((value: any) => {
    setTagFilters(value);
  }, 200);

  const onFinish = async (values: any, publishType?: string) => {
    const isPublish = publishType === "publish";
    const setLoading = isPublish ? setPublishLoading : setSubmitLoading;
    try {
      setLoading(true);

      const tagsArray =
        values?.tags && Array.isArray(values.tags) ? values.tags : [];

      let requestData: any = {
        title: values.title || "",
        description: values.description || description || "",
      };

      if (imageurl) requestData.head = imageurl;
      if (videoUrl) requestData.video = videoUrl;
      if (tagsArray.length > 0) requestData.tags = tagsArray;
      if (values?.permalink) requestData.permalink = values.permalink;
      if (User?.id) requestData.user = User.id;
      if (isPublish) requestData.publishedAt = new Date().toISOString();

      let requestObj = { data: requestData };
      let url = API.REELS;
      const res: any = await POST(url, requestObj);

      if (res?.data?.id) {
        setReelsId(res?.data?.id);
        setLoading(false);
        if (isPublish) {
          message.success("Published");
          props?.onClose();
          props?.reload();
        } else {
          form.resetFields();
          setDescription("");
          setImageurl("");
          setVideoUrl("");
          setImageUploadPercent(0);
          setVideoUploadPercent(0);
          notification.success({ message: "Saved as draft" });
          props?.reload();
        }
      } else {
        notification.error({
          message: "Something went wrong...!",
          description:
            res?.error?.message || "Please check console for details",
        });
        setLoading(false);
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.error?.message ||
        err?.message ||
        "Something went wrong...!";
      notification.error({ message: "Error", description: errorMessage });
      setLoading(false);
    }
  };

  const onPublish = async () => {
    try {
      const values = await form.validateFields();
      if (reelsId) {
        setPublishLoading(true);
        let url = API.BASE_URL + API.REELS + `/${reelsId}`;
        let requestObj = { data: { publishedAt: new Date() } };
        axios({ method: "PUT", url, data: requestObj })
          .then(() => {
            message.success("Published");
            props?.onClose();
            props?.reload();
            setPublishLoading(false);
          })
          .catch((error) => {
            setPublishLoading(false);
            console.log(error);
            message.error("Failed to publish");
          });
      } else {
        await onFinish(values, "publish");
      }
    } catch (error) {
      // Form validation failed
    }
  };

  const handleImageUpload = async (options: any) => {
    const { file } = options;
    setImageUploading(true);
    setImageUploadPercent(0);
    try {
      const response = await uploadImageWithProgress(file, (percent) => {
        setImageUploadPercent(percent);
      });
      if (response) {
        setImageurl(response.data.imageUrl);
      }
    } catch (error) {
      notification.error({ message: "Image upload failed" });
    } finally {
      setImageUploading(false);
    }
  };

  const handleVideoUpload = async (options: any) => {
    const { file } = options;
    setVideoUploading(true);
    setVideoUploadPercent(0);
    try {
      const response = await uploadVideoWithProgress(file, (percent) => {
        setVideoUploadPercent(percent);
      });
      if (response) {
        setVideoUrl(response.data.imageUrl);
      }
    } catch (error) {
      notification.error({ message: "Video upload failed" });
    } finally {
      setVideoUploading(false);
    }
  };

  const removeImage = () => {
    setImageurl("");
    setImageUploadPercent(0);
  };

  const removeVideo = () => {
    setVideoUrl("");
    setVideoUploadPercent(0);
  };

  return (
    <>
      <PageHeader title={"Reel Form"} onBack={() => props?.onClose()} />
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "16px 16px 0",
        }}
      >
        <Card
          style={{
            width: "100%",
            maxWidth: 1000,
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <Form
            layout="vertical"
            onFinish={onFinish}
            form={form}
          >
            {/* Row 1: Title + Permalink */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Title"
                  name="title"
                  rules={[
                    { required: true, message: "Please enter a reel title" },
                  ]}
                >
                  <Input size="large" placeholder="Type the reel name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Permalink" name="permalink">
                  <Input size="large" placeholder="Enter permalink" />
                </Form.Item>
              </Col>
            </Row>

            {/* Row 2: Description + Tags */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Description" name="description">
                  <TextArea
                    rows={3}
                    size="large"
                    placeholder="Add description"
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Tags" name="tags">
                  <Select
                    mode="multiple"
                    size="large"
                    style={{ width: "100%" }}
                    placeholder="Select tags"
                    filterOption={(input: any, option: any) =>
                      option?.children
                        ?.toLowerCase()
                        ?.indexOf(input.toLowerCase()) >= 0
                    }
                    loading={tagsLoading}
                    onSearch={(val) => OnTagSearch(val)}
                  >
                    {tags.length > 0 &&
                      tags.map((item: any) => (
                        <Select.Option key={item.id} value={item.id}>
                          {item.tag}
                        </Select.Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Row 3: Image + Video uploaders side by side */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Cover Image (Thumbnail)">
                  {imageurl ? (
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                        width: 200,
                        height: 316,
                        borderRadius: 8,
                        overflow: "hidden",
                        border: "1px solid #e8e8e8",
                      }}
                    >
                      <img
                        src={imageurl}
                        alt="Cover"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={removeImage}
                        style={{ position: "absolute", top: 6, right: 6 }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div style={{ width: 150 }}>
                      <Dragger
                        accept="image/*"
                        showUploadList={false}
                        customRequest={handleImageUpload}
                        disabled={imageUploading}
                        style={{ borderRadius: 8, height: 266 }}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text" style={{ fontSize: 13 }}>
                          Upload thumbnail
                        </p>
                        {/* <p className="ant-upload-hint" style={{ fontSize: 11 }}>
                          9:16 portrait
                        </p> */}
                      </Dragger>
                      {imageUploading && (
                        <Progress
                          percent={imageUploadPercent}
                          size="small"
                          style={{ marginTop: 8, width: 150 }}
                        />
                      )}
                    </div>
                  )}
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Reel Video">
                  {videoUrl ? (
                    <div
                      style={{
                        position: "relative",
                        width: 200,
                        height: 316,
                        borderRadius: 8,
                        overflow: "hidden",
                        background: "#000",
                      }}
                    >
                      <video
                        controls
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      >
                        <source src={videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                      <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                        onClick={removeVideo}
                        style={{ position: "absolute", top: 6, right: 6 }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div style={{ width: 150 }}>
                      <Dragger
                        accept="video/*"
                        showUploadList={false}
                        customRequest={handleVideoUpload}
                        disabled={videoUploading}
                        style={{ borderRadius: 8, height: 266 }}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text" style={{ fontSize: 13 }}>
                          Upload reel video
                        </p>
                        {/* <p className="ant-upload-hint" style={{ fontSize: 11 }}>
                          9:16 portrait
                        </p> */}
                      </Dragger>
                      {videoUploading && (
                        <Progress
                          percent={videoUploadPercent}
                          size="small"
                          style={{ marginTop: 8, width: 150 }}
                        />
                      )}
                    </div>
                  )}
                </Form.Item>
              </Col>
            </Row>

            {/* Bottom Buttons */}
            <div
              style={{
                borderTop: "1px solid #f0f0f0",
                paddingTop: 16,
                marginTop: 8,
                display: "flex",
                justifyContent: "flex-end",
                gap: 12,
              }}
            >
              <Button
                size="large"
                style={{ borderRadius: 8, height: 44 }}
                onClick={() => props?.onClose()}
                disabled={submitLoading || publishLoading}
              >
                Cancel
              </Button>
              {/* <Button
                size="large"
                htmlType="submit"
                style={{ borderRadius: 8, height: 44 }}
                disabled={submitLoading || publishLoading}
              >
                Save Draft
              </Button> */}
              <Button
                type="primary"
                size="large"
                icon={<UploadOutlined />}
                onClick={onPublish}
                style={{ borderRadius: 8, height: 44 }}
                disabled={submitLoading || publishLoading || imageUploading || videoUploading}
              >
                Publish
              </Button>
            </div>
          </Form>
        </Card>
      </div>

      {/* Modal loading overlay */}
      <Modal
        open={submitLoading || publishLoading}
        footer={null}
        closable={false}
        centered
        width={200}
        bodyStyle={{ textAlign: "center", padding: "32px 0" }}
      >
        <Spin size="large" />
        <div style={{ marginTop: 16, fontSize: 14, color: "#666" }}>
          {publishLoading ? "Publishing..." : "Saving draft..."}
        </div>
      </Modal>
    </>
  );
}

export default NewForm;
